/**
 * Services SEO Page
 * 
 * Dynamic SEO landing pages for services by country, city, and type.
 * URLs: /services/{country}, /services/{country}/{city}, /services/{country}/{city}/{type}
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/lib/context';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Wrench, MapPin, Star, Clock, ArrowRight, Search, 
  ChevronRight, Shield, CheckCircle2, Building2, Users, Globe
} from 'lucide-react';
import { TrustScoreBadge } from '@/components/TrustScoreBadge';

const API = process.env.REACT_APP_BACKEND_URL;

// Service categories with i18n keys
const SERVICE_CATEGORIES = [
  { value: 'construction', labelKey: 'services.categories.construction', icon: '🏗️' },
  { value: 'electrical', labelKey: 'services.categories.electrical', icon: '⚡' },
  { value: 'plumbing', labelKey: 'services.categories.plumbing', icon: '🔧' },
  { value: 'cleaning', labelKey: 'services.categories.cleaning', icon: '🧹' },
  { value: 'moving', labelKey: 'services.categories.moving', icon: '📦' },
  { value: 'renovation', labelKey: 'services.categories.renovation', icon: '🏠' },
  { value: 'landscaping', labelKey: 'services.categories.landscaping', icon: '🌳' },
  { value: 'painting', labelKey: 'services.categories.painting', icon: '🎨' },
  { value: 'carpentry', labelKey: 'services.categories.carpentry', icon: '🪚' },
  { value: 'hvac', labelKey: 'services.categories.hvac', icon: '❄️' }
];

// Countries with i18n keys
const COUNTRIES = [
  { slug: 'finland', code: 'FI', labelKey: 'countries.FI', flag: '🇫🇮' },
  { slug: 'estonia', code: 'EE', labelKey: 'countries.EE', flag: '🇪🇪' },
  { slug: 'sweden', code: 'SE', labelKey: 'countries.SE', flag: '🇸🇪' },
  { slug: 'norway', code: 'NO', labelKey: 'countries.NO', flag: '🇳🇴' },
  { slug: 'germany', code: 'DE', labelKey: 'countries.DE', flag: '🇩🇪' },
  { slug: 'denmark', code: 'DK', labelKey: 'countries.DK', flag: '🇩🇰' }
];

// Cities by country
const CITIES = {
  finland: ['helsinki', 'espoo', 'tampere', 'vantaa', 'oulu', 'turku'],
  estonia: ['tallinn', 'tartu', 'parnu', 'narva'],
  sweden: ['stockholm', 'gothenburg', 'malmo', 'uppsala'],
  norway: ['oslo', 'bergen', 'trondheim', 'stavanger']
};

export default function ServicesSeoPage() {
  const params = useParams();
  const location = useLocation();
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Extract country, city, serviceType from URL path
  // Supports: /services/finland, /services/finland/helsinki, /services/finland/helsinki/plumbing
  const pathParts = location.pathname.split('/').filter(Boolean);
  const country = params.country || pathParts[1] || '';
  const city = params.city || pathParts[2] || '';
  const serviceType = params.serviceType || pathParts[3] || '';

  const normalizedCountry = country?.toLowerCase();
  const normalizedCity = city?.toLowerCase();
  const normalizedType = serviceType?.toLowerCase();
  
  const countryData = COUNTRIES.find(c => c.slug === normalizedCountry);
  const categoryData = SERVICE_CATEGORIES.find(c => c.value === normalizedType);

  // Fetch services
  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);
      try {
        let url = `${API}/api/services?country=${countryData?.code || normalizedCountry}`;
        if (normalizedCity) url += `&city=${normalizedCity}`;
        if (normalizedType) url += `&category=${normalizedType}`;
        url += '&limit=20';
        
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setServices(data.services || data || []);
        }
      } catch (error) {
        console.error('Failed to fetch services:', error);
      } finally {
        setLoading(false);
      }
    };

    if (normalizedCountry) fetchServices();
  }, [normalizedCountry, normalizedCity, normalizedType, countryData]);

  // Format names
  const formatName = (slug) => {
    if (!slug) return '';
    return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // Get SEO data
  const getSeoData = () => {
    const countryName = countryData ? t(countryData.labelKey) : formatName(normalizedCountry);
    const cityName = normalizedCity ? formatName(normalizedCity) : null;
    const typeName = categoryData ? t(categoryData.labelKey) : null;

    let title, description, h1;

    if (typeName && cityName) {
      title = t('seo.services.titleFull', { type: typeName, city: cityName, country: countryName });
      description = t('seo.services.descFull', { type: typeName, city: cityName, country: countryName });
      h1 = t('seo.services.h1Full', { type: typeName, city: cityName });
    } else if (cityName) {
      title = t('seo.services.titleCity', { city: cityName, country: countryName });
      description = t('seo.services.descCity', { city: cityName, country: countryName });
      h1 = t('seo.services.h1City', { city: cityName });
    } else if (typeName) {
      title = t('seo.services.titleType', { type: typeName, country: countryName });
      description = t('seo.services.descType', { type: typeName, country: countryName });
      h1 = t('seo.services.h1Type', { type: typeName, country: countryName });
    } else {
      title = t('seo.services.title', { country: countryName });
      description = t('seo.services.desc', { country: countryName });
      h1 = t('seo.services.h1', { country: countryName });
    }

    return { title, description, h1 };
  };

  const seo = getSeoData();
  const countryName = countryData ? t(countryData.labelKey) : formatName(normalizedCountry);
  const cityName = normalizedCity ? formatName(normalizedCity) : null;

  // Build canonical URL
  let canonicalPath = `/services/${normalizedCountry}`;
  if (normalizedCity) canonicalPath += `/${normalizedCity}`;
  if (normalizedType) canonicalPath += `/${normalizedType}`;
  const canonicalUrl = `https://keikka.com${canonicalPath}`;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{seo.title} | Keikka</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
        <link rel="alternate" hreflang="en" href={`https://keikka.com/en${canonicalPath}`} />
        <link rel="alternate" hreflang="et" href={`https://keikka.com/et/teenused${canonicalPath.replace('/services', '')}`} />
        <link rel="alternate" hreflang="fi" href={`https://keikka.com/fi/palvelut${canonicalPath.replace('/services', '')}`} />
        <link rel="alternate" hreflang="x-default" href={canonicalUrl} />
      </Helmet>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-bold text-brand">KEIKKA</Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/jobs" className="text-muted-foreground hover:text-foreground transition">{t('nav.jobs')}</Link>
              <Link to="/services" className="text-foreground font-medium">{t('nav.services')}</Link>
              <Link to="/auto" className="text-muted-foreground hover:text-foreground transition">{t('nav.autoRepair')}</Link>
              <Link to="/rentals" className="text-muted-foreground hover:text-foreground transition">{t('nav.rentals')}</Link>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6 flex-wrap">
          <Link to="/" className="hover:text-foreground">{t('common.home')}</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/services" className="hover:text-foreground">{t('nav.services')}</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to={`/services/${normalizedCountry}`} className="hover:text-foreground">
            {countryData?.flag} {countryName}
          </Link>
          {cityName && (
            <>
              <ChevronRight className="h-4 w-4" />
              <Link to={`/services/${normalizedCountry}/${normalizedCity}`} className="hover:text-foreground">
                {cityName}
              </Link>
            </>
          )}
          {normalizedType && categoryData && (
            <>
              <ChevronRight className="h-4 w-4" />
              <span className="text-foreground">{categoryData.icon} {t(categoryData.labelKey)}</span>
            </>
          )}
        </nav>

        {/* Hero */}
        <section className="mb-12">
          <div className="bg-gradient-to-r from-brand/10 to-purple-500/10 rounded-2xl p-8 md:p-12">
            <div className="flex items-center gap-2 text-brand mb-4">
              <Wrench className="h-5 w-5" />
              <span className="font-medium">{countryData?.flag} {countryName}{cityName ? ` • ${cityName}` : ''}</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">{seo.h1}</h1>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl">{seo.description}</p>
            
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 text-sm">
                <Shield className="h-4 w-4 text-green-600" />
                <span>{t('services.seo.verifiedProviders')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span>{t('services.seo.freeQuotes')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Star className="h-4 w-4 text-yellow-500" />
                <span>{t('services.seo.ratings')}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('services.seo.searchPlaceholder')}
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button size="lg" onClick={() => navigate('/services/request')} data-testid="request-service-btn">
                {t('services.seo.requestService')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* Categories Grid (if no type selected) */}
        {!normalizedType && (
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6">{t('services.seo.browseCategories')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {SERVICE_CATEGORIES.map((cat) => (
                <Link
                  key={cat.value}
                  to={`/services/${normalizedCountry}${normalizedCity ? `/${normalizedCity}` : ''}/${cat.value}`}
                  className="flex flex-col items-center p-4 rounded-xl border hover:border-brand hover:bg-brand/5 transition-all group"
                  data-testid={`category-${cat.value}`}
                >
                  <span className="text-3xl mb-2">{cat.icon}</span>
                  <span className="text-sm font-medium text-center group-hover:text-brand">{t(cat.labelKey)}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Cities Grid (if no city selected) */}
        {!normalizedCity && CITIES[normalizedCountry] && (
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6">{t('services.seo.browseCities')}</h2>
            <div className="flex flex-wrap gap-3">
              {CITIES[normalizedCountry].map((citySlug) => (
                <Link
                  key={citySlug}
                  to={`/services/${normalizedCountry}/${citySlug}${normalizedType ? `/${normalizedType}` : ''}`}
                  className="px-4 py-2 rounded-full border hover:border-brand hover:bg-brand/5 transition-all"
                >
                  <MapPin className="h-4 w-4 inline mr-1" />
                  {formatName(citySlug)}
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Services List */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">
              {t('services.seo.availableServices', { count: services.length })}
            </h2>
            <Badge variant="outline">{services.length} {t('services.seo.providers')}</Badge>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-6 bg-muted rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-muted rounded w-1/2"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : services.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Wrench className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-medium mb-2">{t('services.seo.noServices')}</h3>
                <p className="text-muted-foreground mb-4">{t('services.seo.noServicesDesc')}</p>
                <Button onClick={() => navigate('/services/request')}>{t('services.seo.postRequest')}</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service) => (
                <Card 
                  key={service.id || service._id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/services/${service.id || service._id}`)}
                  data-testid={`service-${service.id || service._id}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-lg line-clamp-2">{service.title}</CardTitle>
                      {service.trust_score && (
                        <TrustScoreBadge score={service.trust_score} size="sm" t={t} />
                      )}
                    </div>
                    <CardDescription className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {service.city}, {service.country}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{service.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {service.price && (
                          <Badge variant="secondary">€{service.price}</Badge>
                        )}
                        {service.average_rating > 0 && (
                          <span className="flex items-center gap-0.5 text-sm text-yellow-600">
                            <Star className="h-3 w-3 fill-yellow-500" />
                            {service.average_rating.toFixed(1)}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {service.created_at ? new Date(service.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* ============================================================
            HUMAN-GRADE SEO CONTENT BLOCKS
            ============================================================ */}
        
        {/* Local Context Block - Country-specific service information */}
        <section className="mb-12 bg-muted/30 rounded-2xl p-6 md:p-8">
          <h2 className="text-xl font-bold mb-4">
            {t('services.seo.servicesIn', { country: countryName })}
          </h2>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-4">
            <p>{t(`services.seo.contentBlocks.intro.${normalizedCountry}`, { defaultValue: t('services.seo.contentBlocks.intro.default') })}</p>
            <p>{t(`services.seo.contentBlocks.market.${normalizedCountry}`, { defaultValue: t('services.seo.contentBlocks.market.default') })}</p>
          </div>
        </section>

        {/* Service Type Explanation Block (if type selected) */}
        {normalizedType && categoryData && (
          <section className="mb-12">
            <h2 className="text-xl font-bold mb-4">
              {t('services.seo.aboutService', { service: t(categoryData.labelKey) })}
            </h2>
            <div className="prose prose-sm max-w-none text-muted-foreground">
              <p>{t(`services.seo.contentBlocks.serviceTypes.${normalizedType}`, { defaultValue: '' })}</p>
            </div>
          </section>
        )}

        {/* Internal Linking - Related Jobs */}
        <section className="mb-12">
          <h2 className="text-xl font-bold mb-4">{t('services.seo.relatedOpportunities')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to={`/jobs/${normalizedCountry}`}
              className="p-4 rounded-lg border hover:border-brand hover:bg-brand/5 transition-all group"
              data-testid="link-jobs"
            >
              <h3 className="font-medium group-hover:text-brand">{t('nav.jobs')}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t('services.seo.findJobs', { country: countryName })}</p>
            </Link>
            <Link
              to={`/rentals/${normalizedCountry}`}
              className="p-4 rounded-lg border hover:border-brand hover:bg-brand/5 transition-all group"
              data-testid="link-rentals"
            >
              <h3 className="font-medium group-hover:text-brand">{t('nav.rentals')}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t('services.seo.rentEquipment', { country: countryName })}</p>
            </Link>
            <Link
              to="/subcontracting"
              className="p-4 rounded-lg border hover:border-brand hover:bg-brand/5 transition-all group"
              data-testid="link-subcontracting"
            >
              <h3 className="font-medium group-hover:text-brand">{t('nav.subcontracting')}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t('services.seo.subcontractDesc')}</p>
            </Link>
          </div>
        </section>

        {/* Other Countries */}
        <section className="mb-12">
          <h2 className="text-xl font-bold mb-4">{t('services.seo.otherCountries')}</h2>
          <div className="flex flex-wrap gap-2">
            {COUNTRIES.filter(c => c.slug !== normalizedCountry).map((c) => (
              <Link
                key={c.slug}
                to={`/services/${c.slug}${normalizedType ? `/${normalizedType}` : ''}`}
                className="px-4 py-2 rounded-full border hover:border-brand hover:bg-brand/5 transition-all text-sm"
              >
                {c.flag} {t(c.labelKey)}
              </Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-brand/5 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-2">{t('services.seo.ctaTitle')}</h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">{t('services.seo.ctaDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" onClick={() => navigate('/services/request')}>{t('services.seo.requestNow')}</Button>
            <Button variant="outline" size="lg" onClick={() => navigate('/register')}>{t('services.seo.becomeProvider')}</Button>
          </div>
        </section>
      </main>

      <footer className="border-t mt-12 py-8">
        <div className="container mx-auto px-4 text-center text-muted-foreground">
          <p>© {new Date().getFullYear()} Keikka. {t('footer.allRightsReserved')}</p>
        </div>
      </footer>
    </div>
  );
}
